import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Backend /health lives at the API origin root (not under /api/v1).
const HEALTH_URL = `${API_URL.replace(/\/api\/v1\/?$/, '')}/health`;

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Fire-and-forget ping to wake a spun-down Render instance. The free tier
// cold-starts (~30-60s) after 15 min idle, which otherwise makes the first
// login hang. Calling this when the user lands on /login overlaps that boot
// with the time they spend in the Google account picker. `no-cors` keeps the
// browser console clean — we don't read the response, we only need the request
// to reach the server and trigger the boot.
export const warmupBackend = () => {
  try {
    void fetch(HEALTH_URL, { mode: 'no-cors', cache: 'no-store' }).catch(() => {});
  } catch {
    /* ignore — best-effort */
  }
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Only bounce to /login when the user is actually inside the app. On public
      // pages (landing, pricing, etc.) a 401 from the background token-validation
      // should just clear the stale token and let the nav show "Sign In" — not
      // yank a visitor off the marketing site.
      if (window.location.pathname.startsWith('/app')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (data: { email: string; password: string; full_name: string }) =>
    api.post('/auth/register', data),
  googleAuth: (credential: string) =>
    api.post('/auth/google', { credential }),
  getMe: () => api.get('/auth/me'),
};

// Phone verification (WhatsApp OTP) API
export const phoneApi = {
  sendOtp: (phone: string) => api.post('/phone/send-otp', { phone }),
  verifyOtp: (phone: string, code: string) =>
    api.post('/phone/verify-otp', { phone, code }),
};

/** True when a 403 is the free-tier phone-verification gate. */
export function isPhoneGateError(err: any): boolean {
  return (
    err?.response?.status === 403 &&
    err.response.data?.detail?.code === 'phone_verification_required'
  );
}

/** Render an API error detail safely — structured details must never reach
 * JSX raw (React throws on object children). */
export function errorDetailText(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return fallback;
}

// Jobs API
export const jobsApi = {
  getJobs: (skip = 0, limit = 20) =>
    api.get(`/jobs/?skip=${skip}&limit=${limit}`),
  getJob: (id: string) => api.get(`/jobs/${id}`),
  getJobProgress: (id: string) => api.get(`/jobs/${id}/progress`),
  downloadDocx: (id: string) =>
    api.get(`/jobs/${id}/download-docx`, { responseType: 'blob' }),
  createJob: (data: any) => api.post('/jobs/', data),
  deleteJob: (id: string) => api.delete(`/jobs/${id}`),
  assignMatter: (id: string, matterId: string | null) =>
    api.patch(`/jobs/${id}`, { matter_id: matterId }),
  frameQuestion: (
    messages: { role: 'user' | 'assistant'; content: string }[],
    inputDocumentId?: string,
  ) =>
    api.post('/jobs/frame-question', {
      messages,
      ...(inputDocumentId ? { input_document_id: inputDocumentId } : {}),
    }),
};

// Memo interactions API (chat / quiz / debate on completed jobs)
export const interactionsApi = {
  getMessages: (jobId: string, mode?: string) =>
    api.get(`/jobs/${jobId}/messages${mode ? `?mode=${mode}` : ''}`),
  sendMessage: (jobId: string, mode: 'chat' | 'debate', content: string) =>
    api.post(`/jobs/${jobId}/messages`, { mode, content }),
  generateQuiz: (jobId: string) => api.post(`/jobs/${jobId}/quiz`),
  clearMessages: (jobId: string, mode: string) =>
    api.delete(`/jobs/${jobId}/messages?mode=${mode}`),
};

// Drafts API (first-draft legal documents generated from a completed memo)
export const draftsApi = {
  list: (jobId: string) => api.get(`/jobs/${jobId}/drafts`),
  create: (jobId: string, draftType: string, instructions?: string) =>
    api.post(`/jobs/${jobId}/drafts`, {
      draft_type: draftType,
      ...(instructions ? { instructions } : {}),
    }),
  get: (jobId: string, draftId: string) => api.get(`/jobs/${jobId}/drafts/${draftId}`),
  remove: (jobId: string, draftId: string) => api.delete(`/jobs/${jobId}/drafts/${draftId}`),
  downloadDocx: (jobId: string, draftId: string) =>
    api.get(`/jobs/${jobId}/drafts/${draftId}/download-docx`, { responseType: 'blob' }),
};

// Direct Drafts API (memoless drafting from structured facts) + drafts library
export const directDraftsApi = {
  types: () => api.get('/drafts/types'),
  /** One turn of the conversational intake — gathers field values by Q&A.
      No quota; only create() charges. */
  intakeChat: (payload: {
    draft_type: string;
    messages: { role: 'user' | 'assistant'; content: string }[];
    input_document_id?: string;
  }) => api.post('/drafts/intake-chat', payload),
  create: (payload: {
    draft_type: string;
    fields?: Record<string, string>;
    instructions?: string;
    matter_id?: string;
    input_document_id?: string;
    include_authorities?: boolean;
  }) => api.post('/drafts', payload),
  /** All the user's drafts — memo-grounded AND direct. */
  list: (params?: { matter_id?: string; standalone?: boolean }) =>
    api.get('/drafts', { params }),
  get: (draftId: string) => api.get(`/drafts/${draftId}`),
  /** Save a manual edit of the draft text (web editor). Docx export follows. */
  update: (draftId: string, content_md: string) =>
    api.patch(`/drafts/${draftId}`, { content_md }),
  remove: (draftId: string) => api.delete(`/drafts/${draftId}`),
  downloadDocx: (draftId: string) =>
    api.get(`/drafts/${draftId}/download-docx`, { responseType: 'blob' }),
};

// Matters API (client/case workspaces)
export const mattersApi = {
  list: () => api.get('/matters/'),
  create: (data: { title: string; client_name?: string; description?: string }) =>
    api.post('/matters/', data),
  get: (id: string) => api.get(`/matters/${id}`),
  update: (id: string, data: { title?: string; client_name?: string; description?: string }) =>
    api.put(`/matters/${id}`, data),
  remove: (id: string) => api.delete(`/matters/${id}`),
};

// Users API
export const usersApi = {
  updateProfile: (data: {
    full_name?: string;
    organization?: string;
    phone?: string;
    practice_email_reminders?: boolean;
  }) => api.put('/users/me', data),
};

// Practice API (cases / hearings / tasks / collaborators / assistant / calendar)
export const practiceApi = {
  board: () => api.get('/practice/board'),
  listCases: (scope: 'cases' | 'all' = 'cases') =>
    api.get(`/practice/cases?scope=${scope}`),
  createCase: (data: Record<string, any>) => api.post('/practice/cases', data),
  updateCase: (id: string, data: Record<string, any>) =>
    api.patch(`/practice/cases/${id}`, data),
  listHearings: (caseId: string) => api.get(`/practice/cases/${caseId}/hearings`),
  createHearing: (
    caseId: string,
    data: { hearing_date: string; purpose?: string; prep_note?: string },
  ) => api.post(`/practice/cases/${caseId}/hearings`, data),
  updateHearing: (
    hearingId: string,
    data: {
      hearing_date?: string;
      purpose?: string;
      status?: string;
      outcome?: string;
      next_date?: string;
      next_purpose?: string;
      prep_note?: string;
      prep_status?: string;
    },
  ) => api.put(`/practice/hearings/${hearingId}`, data),
  deleteHearing: (hearingId: string) => api.delete(`/practice/hearings/${hearingId}`),
  listTasks: (scope: 'mine' | 'assigned' = 'mine') =>
    api.get(`/practice/tasks?scope=${scope}`),
  createTask: (data: {
    title: string;
    task_type?: string;
    details?: string;
    due_date?: string;
    matter_id?: string;
    assignee_user_id?: string;
  }) => api.post('/practice/tasks', data),
  updateTask: (id: string, data: Record<string, any>) =>
    api.put(`/practice/tasks/${id}`, data),
  deleteTask: (id: string) => api.delete(`/practice/tasks/${id}`),
  listCollaborators: (caseId: string) =>
    api.get(`/practice/cases/${caseId}/collaborators`),
  invite: (caseId: string, email: string) =>
    api.post(`/practice/cases/${caseId}/collaborators`, { email }),
  revoke: (caseId: string, collabId: string) =>
    api.delete(`/practice/cases/${caseId}/collaborators/${collabId}`),
  /** Only call when `user.features?.cnr_lookup` — 404 when the feature is off. */
  cnrLookup: (cnr: string) => api.post('/practice/cases/cnr-lookup', { cnr }),
  assistant: (payload: {
    messages: { role: 'user' | 'assistant'; content: string }[];
    matter_id?: string;
    hearing_id?: string;
  }) => api.post('/practice/assistant', payload),
  assistantExecute: (action: { type: string; params: Record<string, any> }) =>
    api.post('/practice/assistant/execute', { action }),
  getCalendarToken: () => api.get('/practice/calendar/token'),
  rotateCalendarToken: () => api.post('/practice/calendar/token'),
};

// Clients API (Practice contact book)
export const clientsApi = {
  list: () => api.get('/clients/'),
  create: (data: {
    name: string;
    phone?: string;
    whatsapp?: string;
    email?: string;
    address?: string;
    notes?: string;
  }) => api.post('/clients/', data),
  get: (id: string) => api.get(`/clients/${id}`),
  cases: (id: string) => api.get(`/clients/${id}/cases`),
  update: (id: string, data: Record<string, any>) => api.put(`/clients/${id}`, data),
  remove: (id: string) => api.delete(`/clients/${id}`),
};

// Payments API
export const paymentsApi = {
  createOrder: (plan: string) =>
    api.post('/payments/create-order', { plan }),
  verifyPayment: (data: {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
    plan: string;
  }) => api.post('/payments/verify', data),
};

// Public content pages (pSEO — published by the marketing agent)
export const contentApi = {
  getPages: (pageType?: string) =>
    api.get(`/content/pages${pageType ? `?page_type=${pageType}` : ''}`),
  getPage: (slug: string) => api.get(`/content/pages/${slug}`),
};

// Product feedback (beta) — public submission
export const feedbackApi = {
  submit: (data: {
    message: string;
    category?: string;
    rating?: number | null;
    email?: string | null;
    page?: string | null;
  }) => api.post('/feedback', data),
};

// Documents API
export const documentsApi = {
  uploadDocument: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getDocuments: () => api.get('/documents/'),
  getDocument: (id: string) => api.get(`/documents/${id}`),
};
